import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'create-test-by-ai',
    templateUrl: './create-test-by-ai.component.html',
    styleUrls: ['./create-test-by-ai.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateTestByAi implements OnInit, OnDestroy, AfterViewInit {
    public testQsForm: FormGroup;
    private destroy: Subject<boolean> = new Subject<boolean>();
    @Input() hasCreateQuizCheckBox = true;
    @Input() hasCreateMaterialCheckBox = false;
    @Output() valuesChange: EventEmitter<{
        question_count: number;
        difficulty_level: number;
        create_course: boolean;
        create_material: boolean;
        create_quiz: boolean;
        question_type_value: string;
    }> = new EventEmitter<{
        question_count: number;
        difficulty_level: number;
        create_course: boolean;
        create_material: boolean;
        create_quiz: boolean;
        question_type_value: string;
    }>();
    public showOptions: boolean = true;

    constructor(protected fb: FormBuilder) {
        this.testQsForm = fb.group({
            question_count: [10],
            difficulty_level: [3],
            question_type_value: this.fb.group(
                {
                    radio: [true],
                    checkbox: [true],
                    input: [false],
                },
                { validators: this.requireCheckboxesToBeChecked }
            ),
            creationForm: fb.group(
                {
                    create_course: [false],
                    create_material: [true],
                    create_quiz: [true],
                },
                { validators: [this.requireMaterialOrQuiz()] }
            ),
        });
    }

    public get creationForm(): FormGroup {
        return this.testQsForm.get('creationForm') as FormGroup;
    }

    ngOnInit(): void {
        this.testQsForm.valueChanges.pipe(takeUntil(this.destroy)).subscribe((val) => {
            this.valuesChange.emit(this.updateEmittedValue(val));
        });

        this.creationForm.valueChanges.pipe(takeUntil(this.destroy)).subscribe((val) => {
            const createMaterial = !!val?.create_material;
            const createQuiz = !!val?.create_quiz;

            // If user tries to turn everything off, keep at least one option on.
            if (!createMaterial && !createQuiz) {
                this.creationForm.patchValue({ create_material: true }, { emitEvent: false });
                this.creationForm.updateValueAndValidity({ emitEvent: false });
            }
        });
    }
    requireCheckboxesToBeChecked(group: FormGroup) {
        const checked = Object.keys(group.controls).some((key) => group.get(key).value);
        return checked ? null : { requireCheckboxesToBeChecked: true };
    }

    private requireMaterialOrQuiz(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const group = control as FormGroup;
            const createMaterial = !!group.get('create_material')?.value;
            const createQuiz = !!group.get('create_quiz')?.value;

            return createMaterial || createQuiz ? null : { requireMaterialOrQuiz: true };
        };
    }
    ngAfterViewInit(): void {
        this.valuesChange.emit(this.updateEmittedValue(this.testQsForm.value));
    }
    updateEmittedValue(value: any) {
        const selectedQsTypes = [];
        Object.keys(this.testQsForm.value.question_type_value).forEach((key) => {
            if (this.testQsForm.get('question_type_value').get(key).value) {
                selectedQsTypes.push(key);
            }
        });
        const creationFormValue = (value?.creationForm ?? {}) as {
            create_course?: boolean;
            create_material?: boolean;
            create_quiz?: boolean;
        };

        return {
            ...value,
            ...creationFormValue,
            question_type_value: selectedQsTypes.join(','),
        };
    }
    ngOnDestroy(): void {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
